import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Card,
  CardContent,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Switch,
  FormControlLabel
} from '@mui/material';
import { GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Visibility as ViewIcon,
  CheckCircle as ResolveIcon,
  BugReport as BugIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import DataTable from '../components/DataTable';
import { firebaseIssuesService, Issue, IssueComment, IssueFilters } from '../services/firebaseIssues';
import { useDateFormat } from '../hooks/useDateFormat';

const Issues: React.FC = () => {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<IssueFilters>({});
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);
  const [sourceFilter, setSourceFilter] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [stats, setStats] = useState<any>(null);
  
  // Dialog states
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [comments, setComments] = useState<IssueComment[]>([]);
  
  // Form states
  const [editForm, setEditForm] = useState<{
    status: 'new' | 'in_progress' | 'waiting' | 'done';
    priority: 'low' | 'medium' | 'high' | 'critical';
    tags: string[];
  }>({
    status: 'new',
    priority: 'medium',
    tags: []
  });
  const [newComment, setNewComment] = useState('');
  const [formatEscapes, setFormatEscapes] = useState(false);
  
  const { formatDateOnly, formatDateTime } = useDateFormat();

  useEffect(() => {
    // Apply checkbox filters to the main filters object
    const updatedFilters = {
      ...filters,
      status: statusFilter.length > 0 ? statusFilter.join(',') : undefined,
      priority: priorityFilter.length > 0 ? priorityFilter.join(',') : undefined,
      source: sourceFilter.length > 0 ? sourceFilter.join(',') : undefined,
    };
    
    fetchIssues(updatedFilters);
    fetchAvailableTags();
    fetchStats();
  }, [filters, statusFilter, priorityFilter, sourceFilter]);

  const fetchIssues = async (currentFilters?: IssueFilters) => {
    try {
      setLoading(true);
      const data = await firebaseIssuesService.getIssues(currentFilters || filters);
      setIssues(data);
    } catch (err) {
      setError('Failed to fetch issues');
      console.error('Error fetching issues:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to refresh with current filter state
  const refreshWithCurrentFilters = async () => {
    const currentFilters = {
      ...filters,
      status: statusFilter.length > 0 ? statusFilter.join(',') : undefined,
      priority: priorityFilter.length > 0 ? priorityFilter.join(',') : undefined,
      source: sourceFilter.length > 0 ? sourceFilter.join(',') : undefined,
    };
    await fetchIssues(currentFilters);
  };

  const fetchAvailableTags = async () => {
    try {
      const tags = await firebaseIssuesService.getAvailableTags();
      setAvailableTags(tags);
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const statsData = await firebaseIssuesService.getIssueStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };


  const handleViewDetails = async (issue: Issue) => {
    setSelectedIssue(issue);
    try {
      const issueComments = await firebaseIssuesService.getComments(issue.id);
      setComments(issueComments);
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
    setOpenDetailsDialog(true);
  };

  const handleEditIssue = (issue: Issue) => {
    setSelectedIssue(issue);
    setEditForm({
      status: issue.status,
      priority: issue.priority,
      tags: issue.tags || []
    });
    setOpenEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedIssue) return;

    try {
      await firebaseIssuesService.updateIssue(selectedIssue.id, {
        status: editForm.status,
        priority: editForm.priority,
        tags: editForm.tags
      });

      setOpenEditDialog(false);
      await refreshWithCurrentFilters();
      await fetchStats();
    } catch (err) {
      setError('Failed to update issue');
      console.error('Error updating issue:', err);
    }
  };

  const handleDeleteIssue = async (issueId: string) => {
    if (window.confirm('Are you sure you want to delete this issue?')) {
      try {
        await firebaseIssuesService.deleteIssue(issueId);
        await refreshWithCurrentFilters();
        await fetchStats();
      } catch (err) {
        setError('Failed to delete issue');
        console.error('Error deleting issue:', err);
      }
    }
  };

  const handleAddComment = async () => {
    if (!selectedIssue || !newComment.trim()) return;

    try {
      await firebaseIssuesService.addComment(selectedIssue.id, {
        text: newComment.trim(),
        author: 'admin',
        authorName: 'Admin'
      });
      
      setNewComment('');
      // Refresh comments
      const issueComments = await firebaseIssuesService.getComments(selectedIssue.id);
      setComments(issueComments);
    } catch (err) {
      setError('Failed to add comment');
      console.error('Error adding comment:', err);
    }
  };

  const handleResolveIssue = async (issue: Issue) => {
    try {
      await firebaseIssuesService.updateIssue(issue.id, {
        status: 'done'
      });

      await refreshWithCurrentFilters();
      await fetchStats();
    } catch (err) {
      setError('Failed to resolve issue');
      console.error('Error resolving issue:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'default';
      case 'in_progress': return 'primary';
      case 'waiting': return 'warning';
      case 'done': return 'success';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'crashes': return '💥';
      case 'user_feedback': return '💬';
      case 'debug': return '🐛';
      case 'analytics': return '📊';
      default: return '📄';
    }
  };

  const formatOriginalData = (data: any, formatEscapes: boolean = false) => {
    if (!data) return 'No data available';
    
    // Helper function to unescape escape sequences in strings
    const unescapeString = (str: string): string => {
      // Replace escape sequences with their actual characters
      // Order matters: handle \r\n first, then individual sequences, then escaped backslash
      return str
        .replace(/\\r\\n/g, '\r\n')  // \r\n (carriage return + line feed)
        .replace(/\\n/g, '\n')        // \n (line feed)
        .replace(/\\r/g, '\r')        // \r (carriage return)
        .replace(/\\t/g, '\t')        // \t (tab)
        .replace(/\\\\/g, '\\');      // \\ (escaped backslash - must be last)
    };
    
    // Helper function to escape HTML special characters
    const escapeHtml = (str: string): string => {
      const map: { [key: string]: string } = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      };
      return str.replace(/[&<>"']/g, (m) => map[m]);
    };
    
    // Convert to formatted string with proper spacing
    const formatValue = (key: string, value: any, indent = 0): string => {
      const spaces = '  '.repeat(indent);
      
      if (value === null || value === undefined) {
        return `${spaces}${key}: null`;
      }
      
      if (typeof value === 'object' && !Array.isArray(value)) {
        const entries = Object.entries(value);
        if (entries.length === 0) {
          return `${spaces}${key}: {}`;
        }
        const formattedEntries = entries.map(([k, v]) => formatValue(k, v, indent + 1));
        return `${spaces}${key}:\n${formattedEntries.join('\n')}`;
      }
      
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return `${spaces}${key}: []`;
        }
        const formattedItems = value.map((item, index) => 
          formatValue(`[${index}]`, item, indent + 1)
        );
        return `${spaces}${key}:\n${formattedItems.join('\n')}`;
      }
      
      if (typeof value === 'string') {
        // Handle URLs
        if (value.startsWith('http://') || value.startsWith('https://')) {
          return `${spaces}${key}: <a href="${escapeHtml(value)}" target="_blank" rel="noopener noreferrer">${escapeHtml(value)}</a>`;
        }
        
        // Handle escape sequence formatting
        if (formatEscapes) {
          const unescaped = unescapeString(value);
          // Escape HTML to prevent XSS, but preserve newlines and tabs
          const htmlEscaped = escapeHtml(unescaped);
          return `${spaces}${key}: ${htmlEscaped}`;
        } else {
          // Raw mode: show as JSON string
          return `${spaces}${key}: ${JSON.stringify(value)}`;
        }
      }
      
      return `${spaces}${key}: ${JSON.stringify(value)}`;
    };
    
    const formattedEntries = Object.entries(data).map(([key, value]) => 
      formatValue(key, value, 0)
    );
    
    return formattedEntries.join('\n');
  };

  const formatCommentTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const getColumns = (): GridColDef[] => [
    { 
      field: 'createdAt', 
      headerName: 'Created', 
      width: 160,
      valueFormatter: (params) => {
        if (!params.value) return 'Unknown';
        const date = new Date(params.value);
        return date.toLocaleString('en-GB', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      }
    },
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value.substring(0, 8)}...
        </Typography>
      )
    },
    { 
      field: 'title', 
      headerName: 'Title', 
      flex: 2,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" noWrap>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {getSourceIcon(params.row.source)} {params.row.source}
          </Typography>
        </Box>
      )
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
        />
      )
    },
    { 
      field: 'priority', 
      headerName: 'Priority', 
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getPriorityColor(params.value)}
          size="small"
          variant="outlined"
        />
      )
    },
    { 
      field: 'tags', 
      headerName: 'Tags', 
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {params.value?.slice(0, 2).map((tag: string) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              variant="outlined"
            />
          ))}
          {params.value?.length > 2 && (
            <Chip
              label={`+${params.value.length - 2}`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<ViewIcon />}
          label="View Details"
          onClick={() => handleViewDetails(params.row)}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleEditIssue(params.row)}
        />,
        <GridActionsCellItem
          icon={<ResolveIcon />}
          label="Resolve"
          onClick={() => handleResolveIssue(params.row)}
          disabled={params.row.status === 'done'}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteIssue(params.row.id)}
        />
      ]
    }
  ];

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            Issues Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Platform Admin - Firebase Issues Dashboard
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={refreshWithCurrentFilters}
                disabled={loading}
              >
                Refresh
              </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Issues
                </Typography>
                <Typography variant="h4">
                  {stats.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  New Issues
                </Typography>
                <Typography variant="h4" color="primary">
                  {stats.byStatus.new || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  In Progress
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.byStatus.in_progress || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Resolved
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.byStatus.done || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                multiple
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                renderValue={(selected) => selected.length === 0 ? 'All' : `${selected.length} selected`}
              >
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="waiting">Waiting</MenuItem>
                <MenuItem value="done">Done</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                multiple
                value={priorityFilter}
                label="Priority"
                onChange={(e) => setPriorityFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                renderValue={(selected) => selected.length === 0 ? 'All' : `${selected.length} selected`}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select
                multiple
                value={sourceFilter}
                label="Source"
                onChange={(e) => setSourceFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                renderValue={(selected) => selected.length === 0 ? 'All' : `${selected.length} selected`}
              >
                <MenuItem value="analytics">Analytics</MenuItem>
                <MenuItem value="crashes">Crashes</MenuItem>
                <MenuItem value="debug">Debug</MenuItem>
                <MenuItem value="user_feedback">User Feedback</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              size="small"
              options={availableTags}
              value={filters.tags || []}
              onChange={(_, newValue) => setFilters({ ...filters, tags: newValue })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder="Select tags"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Search title, description, tags..."
              value={filters.search || ''}
              onChange={(e) => setFilters({ ...filters, search: e.target.value || undefined })}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Issues Table */}
      <DataTable
        rows={issues}
        columns={getColumns()}
        loading={loading}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 25 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        getRowHeight={() => 'auto'}
        sx={{
          '& .MuiDataGrid-row': {
            minHeight: '60px !important',
            maxHeight: 'none !important',
          },
          '& .MuiDataGrid-cell': {
            minHeight: '60px !important',
            maxHeight: 'none !important',
            padding: '8px 16px',
          }
        }}
      />

      {/* Issue Details Dialog */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BugIcon />
            Issue Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedIssue && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedIssue.title}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip label={selectedIssue.status} color={getStatusColor(selectedIssue.status)} />
                <Chip label={selectedIssue.priority} color={getPriorityColor(selectedIssue.priority)} variant="outlined" />
                <Chip label={selectedIssue.source} variant="outlined" />
              </Box>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Created: {selectedIssue.createdAt ? formatCommentTimestamp(selectedIssue.createdAt) : 'Unknown'}
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                <Typography variant="h6">
                  Original Data
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formatEscapes}
                      onChange={(e) => setFormatEscapes(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Format escape sequences"
                />
              </Box>
              <Box 
                component="pre" 
                sx={{ 
                  backgroundColor: 'grey.100', 
                  p: 2, 
                  borderRadius: 1, 
                  overflow: 'auto',
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  '& a': {
                    color: 'primary.main',
                    textDecoration: 'underline'
                  }
                }}
                dangerouslySetInnerHTML={{ __html: formatOriginalData(selectedIssue.originalData, formatEscapes) }}
              />
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                Comments ({comments.length})
              </Typography>
              
              <List>
                {comments.map((comment) => (
                  <ListItem key={comment.id} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {comment.text}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatCommentTimestamp(comment.timestamp)}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
                {comments.length === 0 && (
                  <ListItem>
                    <ListItemText primary="No comments yet" />
                  </ListItem>
                )}
              </List>
              
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Add Comment"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                />
                <Button
                  variant="contained"
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                  sx={{ mt: 1 }}
                >
                  Add Comment
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Issue Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Issue</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={editForm.status}
                label="Status"
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value as any })}
              >
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="waiting">Waiting</MenuItem>
                <MenuItem value="done">Done</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                value={editForm.priority}
                label="Priority"
                onChange={(e) => setEditForm({ ...editForm, priority: e.target.value as any })}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
            
            <Autocomplete
              multiple
              options={availableTags}
              value={editForm.tags}
              onChange={(_, newValue) => setEditForm({ ...editForm, tags: newValue })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder="Select or add tags"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Issues;
